import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Text } from '@chakra-ui/react';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { getSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { httpsAgent } from '@/lib';
import { withSession } from '@/lib/session';
import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';

import { H1 } from '@/components/Heading';
import NotFound from '@/components/NotFound';
import Seo from '@/components/Seo';
import { SortingProducts, SortOptionValue, SortParams } from '@/components/Sorting/SortingProducts';
import { SubcategoryTiles } from '@/components/SubcategoryTiles/SubcategoryTiles';
import { Button } from '@/components/UI/Buttons/Button';

import { ProductList } from '@/layouts/ProductsSection/ProductList';
import { OptionsWithServerSideRequest, Product } from '@/models/api';
import { LocaleProps } from '@/models/props/LocalizationProps';
import { CategoryProductListProps, Sort, SortDir } from '@/models/props/ProductCardProps';
import { ProductCategoryProps } from '@/models/props/ProductCategoryProps';
import { CategoryService, ProductService } from '@/services';

type CategoryPageProps = {
    productList: CategoryProductListProps;
    categoryData: ProductCategoryProps;
    user: OptionsWithServerSideRequest;
    sort: Sort;
    sortDir: SortDir;
};

export const CategoryPage = (props: CategoryPageProps) => {
    const { productList, categoryData, user } = props;
    const router = useRouter();
    const { items, currentPage, totalPageCount, totalCount } = productList;
    const [categoryName, setCategoryName] = useState(categoryData?.name);
    const [currentPageNo, setCurrentPageNo] = useState(currentPage);
    const [currentProductList, setCurrentProductList] = useState<Product[]>(items);
    const [sort, setSort] = useState<Sort | undefined>(props.sort);
    const [sortDir, setSortDir] = useState<SortDir | undefined>(props.sortDir);
    const [loading, setLoading] = useState(false);
    const intl = useIntl();

    const obj = useMemo(
        () => ({
            id: categoryData?.id,
            name: categoryData?.name,
            user,
            totalPageCount: totalPageCount
        }),
        [categoryData, user, totalPageCount]
    );

    const fetchProductsHandler = useCallback(
        async (pageNo: number, sort?: Sort, sortDir?: SortDir) => {
            let loadMoreProducts: CategoryProductListProps;

            try {
                loadMoreProducts = await ProductService.getProductsList({
                    pageNo: pageNo,
                    categoryId: [obj.id.toString()],
                    sort: sort,
                    sortDir: sortDir,
                    opts: obj.user as OptionsWithServerSideRequest
                });

                return loadMoreProducts;
            } catch (e) {
                return (loadMoreProducts = {
                    items: [],
                    currentPage: pageNo,
                    totalPageCount: 0,
                    totalCount: 0
                });
            } finally {
                setLoading(false);
            }
        },
        [obj]
    );

    const sortCallback = useCallback(async (value: SortOptionValue) => {
        const sort = value.sort;
        const sortDir = value.sortDir;

        setCurrentPageNo(1);

        setSortDir(() => sortDir);

        setSort(() => sort);
    }, []);

    const loadMoreHandler = useCallback(
        async (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.preventDefault();

            setLoading(true);

            setCurrentPageNo(currentPageNo + 1);

            const productList = await fetchProductsHandler(currentPageNo + 1, sort, sortDir);
            const newItems = [...currentProductList, ...productList.items];

            setCurrentProductList(newItems);
        },
        [fetchProductsHandler, currentProductList, sort, sortDir, currentPageNo]
    );

    useEffect(() => {
        setCurrentProductList(items);
        setCategoryName(obj.name);
    }, [router.query, obj.name, items]);

    return (
        <>
            <Seo title={categoryName} />
            <Container maxW="container.3xl" px={8} pb={14} pt={8}>
                <Box>
                    <H1 textAlign="center" mb={3}>
                        {categoryName}
                    </H1>
                </Box>

                {categoryData.subs && categoryData.subs.length > 0 && (
                    <SubcategoryTiles subcategories={categoryData.subs} />
                )}

                {currentProductList.length > 0 && (
                    <Flex
                        my={6}
                        flexDirection={{ base: 'column', md: 'row' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <SortingProducts
                            sort={sort}
                            sortDir={sortDir}
                            selectValue={sortCallback}
                        ></SortingProducts>
                        <Text textColor="grey.600" mt={{ base: 4, md: 0 }}>
                            <FormattedMessage id="total-count"></FormattedMessage>: {totalCount}
                        </Text>
                    </Flex>
                )}

                <Box pt={6}>
                    {currentProductList.length > 0 ? (
                        <ProductList items={currentProductList} />
                    ) : (
                        <NotFound message="product.category.not-found" />
                    )}
                </Box>
                {currentPageNo < obj.totalPageCount && (
                    <>
                        <Flex justifyContent="center" pt={16}>
                            <Button
                                onClick={loadMoreHandler}
                                fontSize="xl"
                                h={20}
                                py={3}
                                px={12}
                                rounded="full"
                                rightIcon={<ChevronDownIcon />}
                                isLoading={loading}
                                spinnerPlacement="end"
                                loadingText={intl.formatMessage({ id: 'loading' })}
                            >
                                <FormattedMessage id="see-more-products" />
                            </Button>
                        </Flex>
                    </>
                )}
            </Container>
        </>
    );
};

export const getServerSideProps = withCommonServerSideProps(
    withSession(async (context: GetServerSidePropsContext) => {
        const session = await getSession(context);
        const user = session && httpsAgent.getAuthHeaders({ session });
        const { query, locale } = context;
        const category = query.category as string;
        let categoryData: ProductCategoryProps | null;
        let productList: CategoryProductListProps;
        const searchQuery = context.query as SortParams;
        const sort = searchQuery.sort;
        const sortDir = searchQuery.sortDir;

        try {
            categoryData = await CategoryService.getCategoryBySlug(category).then(
                (response) => response.data[0]
            );
        } catch {
            return {
                notFound: true
            };
        }

        try {
            productList = await ProductService.getProductsList({
                categoryId: [categoryData?.id.toString() || ''],
                locale: locale as LocaleProps,
                opts: {
                    session
                },
                sort,
                sortDir
            });
        } catch {
            productList = { currentPage: 1, items: [], totalCount: 0, totalPageCount: 1 };
            categoryData = null;
        }

        return {
            props: {
                productList,
                categoryData,
                user,
                sort: sort || null,
                sortDir: sortDir || null
            }
        };
    })
);

export default CategoryPage;
